import React, { useContext, useEffect, useMemo, useState } from "react";
import ConsentAndRejectModal from "./ConsentAndRejectModal";
import { SystemPurpose, VendorWrapper, ActionExtension } from "../../../../../../records/us_privacy_regulation_records";
import { RenderLimitedChips, message } from "../../../../../../styleguide";
import { Table, Button, Tabs, Input,  Tag, Flex, Typography, Divider, Tooltip } from "antd";
import { CopyOutlined,  DeleteOutlined,  EditOutlined } from "@ant-design/icons";
import {  getVendorIcon, wrapInInfotip, getVendorType } from "../../../helper";
import { useDebouncedSearch } from "../../../../../../hooks";
import { trimString } from "../../../../../utils";
import { usPrivacyRegulationContext } from "../../../contexts";

import DismissErrorModal from "../../../../../common/DismissErrorModal";
import { ConfirmationWrapper } from "../../../../helper";
import SVG from "react-inlinesvg";
import { EllipseIcon } from "../../../../../../assets/icons/EllipseIcon";

const getActionsArray = (consentActions, extenstions) => {
  let arr = [];
  consentActions.forEach((action) => {
    if (action.url) {
      arr.push("Inline URL");
    } else if (action.tagManager) {
      arr.push("Google Tag Manager");
    } else if (action.type == "inline" && Boolean(action.js)) {
      arr.push("Custom JS");
    }
  });
  if (extenstions.cookies.length) {
    arr.push("Cookies");
  }
  return [...new Set(arr)];
};

const recordHasHooks = (record) => {
  return getActionsArray(record.consentActions, record.consentActionExtension).length || getActionsArray(record.rejectActions, record.rejectActionExtension).length 
}

const OptInAndOptOut = (props) => {
  const [makeDataSourceRender, setMakeDataSourceRender] = useState(false);

  const [activeTabKey, setActiveTabKey] = useState("Vendor");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [editModal, showEditModal] = useState(false);
  const [editPurpose, setEditPurpose] = useState({});
  const [editPurposeId, setEditPurposeId] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [addNewHookModal, showAddNewHookModal] = useState(false);
  const [removeHooksPrompt, setRemoveHooksPrompt] = useState(false);
  const [vendorTypeFilter, setVendorTypeFilter] = useState([]);
  
  const { vendorsIdsMap, isGlobalStandardFlow } = useContext(usPrivacyRegulationContext);

  useEffect(()=>{
    setSelectedRowKeys([])
  },[activeTabKey])

  const onEditPurpose = (record) => {
    showEditModal(true);
    setEditPurpose(activeTabKey == "Vendor" ? new VendorWrapper(record) : new SystemPurpose(record));
    setEditPurposeId(activeTabKey == "Vendor" ? record.vendorId : record.id ?? record.privacyChoice)
  };

  const deleteHooks = (record) => {
    const recordId = activeTabKey == "Vendor" ? record.vendorId : record.id ?? record.privacyChoice;
    record.consentActions = [];
    record.rejectActions = [];
    record.consentActionExtension = new ActionExtension({})?.toJS();
    record.rejectActionExtension = new ActionExtension({})?.toJS();

    if(activeTabKey == "Vendor"){
      const updatedVendorsWrappers = _.cloneDeep(props.usPrivacyRegulation.vendorsWrappers);
      const idx = updatedVendorsWrappers.findIndex(vw => vw.vendorId == recordId);
      if(idx !== -1){
        props.usPrivacyRegulation.vendorsWrappers = [
          ...updatedVendorsWrappers.slice(0, idx),
          record,
          ...updatedVendorsWrappers.slice(idx + 1),
        ];
      }
    } else {
      const updatedCategories = _.cloneDeep(props.usPrivacyRegulation.categories);
      const idx = updatedCategories.findIndex(cat => (cat.id ?? cat.privacyChoice) == recordId);
      if(idx !== -1){
        props.usPrivacyRegulation.categories = [
          ...updatedCategories.slice(0, idx),
          record,
          ...updatedCategories.slice(idx + 1),
        ];
      }
    }
    setMakeDataSourceRender(!makeDataSourceRender)
    setSelectedRowKeys(selectedRowKeys.filter( key => key != recordId))
  }

  const closeModal = () => {
    showEditModal(false);
    showAddNewHookModal(false);
  };

  const handlePurposeSave = (purpose, purposeId) => {
    setEditPurpose(purpose);
    if(activeTabKey == "Vendor"){
      const updatedVendorsWrappers = _.cloneDeep(props.usPrivacyRegulation.vendorsWrappers);
      const idx = updatedVendorsWrappers.findIndex(vw => vw.vendorId == purposeId);
      if(idx !== -1){
        props.usPrivacyRegulation.vendorsWrappers = [
          ...updatedVendorsWrappers.slice(0, idx),
          purpose.toJS(),
          ...updatedVendorsWrappers.slice(idx + 1),
        ];
      }
    } else {
      const updatedCategories = _.cloneDeep(props.usPrivacyRegulation.categories);
      const idx = updatedCategories.findIndex(cat => (cat.id ?? cat.privacyChoice) == purposeId);
      if(idx !== -1){
        props.usPrivacyRegulation.categories = [
          ...updatedCategories.slice(0, idx),
          purpose.toJS(),
          ...updatedCategories.slice(idx + 1),
        ];
      }
    }
    showEditModal(false);
    showAddNewHookModal(false);
    setMakeDataSourceRender(!makeDataSourceRender)
  };

  const removeHooks = () => {
    if(activeTabKey == "Vendor"){
      let updatedVendorsWrappers = _.cloneDeep(props.usPrivacyRegulation.vendorsWrappers);
      updatedVendorsWrappers = updatedVendorsWrappers.map(vw => {
        let updatedVw = _.cloneDeep(vw);
        if(selectedRowKeys.includes(updatedVw.vendorId)){
          updatedVw.consentActions = [];
          updatedVw.rejectActions = [];
          updatedVw.consentActionExtension = new ActionExtension({})?.toJS();
          updatedVw.rejectActionExtension = new ActionExtension({})?.toJS();
        }
        return updatedVw;
      })
      props.usPrivacyRegulation.vendorsWrappers = updatedVendorsWrappers;
      message.success(<>Successfully removed hooks for <b>{selectedRowKeys.length}</b> vendors.</>)
    } else {
      let updatedCategories = _.cloneDeep(props.usPrivacyRegulation.categories);
      updatedCategories = updatedCategories.map(cat => {
        let updatedCat = _.cloneDeep(cat);
        if(selectedRowKeys.includes(updatedCat.id ?? updatedCat.privacyChoice)){
          updatedCat.consentActions = [];
          updatedCat.rejectActions = [];
          updatedCat.consentActionExtension = new ActionExtension({})?.toJS();
          updatedCat.rejectActionExtension = new ActionExtension({})?.toJS();
        }
        return updatedCat;
      })
      props.usPrivacyRegulation.categories = updatedCategories;
      message.success(<>Successfully removed hooks for <b>{selectedRowKeys.length}</b> privacy choices.</>)
    }
    setSelectedRowKeys([])
    setMakeDataSourceRender(!makeDataSourceRender)
    setRemoveHooksPrompt(false);
  }

  const handleCopyClick = async (elementId) => {
    try {
      const textToCopy = document.getElementById(elementId).innerText;
      await navigator.clipboard.writeText(textToCopy);
      message.info('Vendor Id copied', 1)
    } catch (err) {
      message.info('Failed to copy Vendor Id', 1)
    }
  };

  let columns = [
    {
      title: "Vendor",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      filters: isGlobalStandardFlow ? null : [
        {
          text: <><EllipseIcon className="ellipse-icon cyan-7"/> MSPA Signatory / Certified</>,
          value: 'MSPA',
        },
        {
          text: <><EllipseIcon className="ellipse-icon magenta-6"/> Custom</>,
          value: 'CUSTOM',
        },
      ],
      filterMultiple: false,
      render: (name, vendorRecord) => {
        const icon = isGlobalStandardFlow ? null : getVendorIcon(vendorRecord);
        const elementId = `vendor_id_${vendorRecord.vendorId}`;
        const content = (
          <div className="vendor-name-infotip">
            {isGlobalStandardFlow ? null : (
              <p>
                {icon}{" "}
                {vendorRecord.ids?.mspaId
                  ? "MSPA Signatory / Certified"
                  : "Custom"}
              </p>
            )}
            <span>
              ID:{" "}
              <span id={elementId} className="vendor-id">
                {vendorRecord.vendorId}
              </span>
            </span>
            <CopyOutlined onClick={() => handleCopyClick(elementId)} />
          </div>
        );
        return wrapInInfotip(
          <span>
            {icon} {trimString(name, 20)}
          </span>,
          content
        );
      }
    },
    {
      title: "Privacy Choice",
      dataIndex: "privacyChoice",
      key: "privacyChoice",
      render: (text, record) => record.privacyChoice ?? record.name,
    },
    {
      title: "Opt In Hooks",
      dataIndex: "consentActions",
      key: "consentActions",
      render: (consentActions, PurposeRecord) => {
        let chips =  getActionsArray(consentActions, PurposeRecord.consentActionExtension);
        chips = chips.map(ele => <Tag>{ele}</Tag>)
        return <RenderLimitedChips chips={chips} cutOff={3}/>
      }
    },
    {
      title: "Opt Out Hooks",
      dataIndex: "rejectActions",
      key: "rejectActions",
      render: (rejectActions, PurposeRecord) => {
        let chips =   getActionsArray(rejectActions, PurposeRecord.rejectActionExtension);
        chips = chips.map(ele => <Tag>{ele}</Tag>)
        return <RenderLimitedChips chips={chips} cutOff={3}/>
      }

    },
  ];

  let hooksModalRecords = []
  switch(activeTabKey) {
    case 'Vendor':
      columns =  columns.filter(column => column.dataIndex !== 'privacyChoice');
      hooksModalRecords = props.usPrivacyRegulation.vendorsWrappers.map(vw => ({ ...vw, ids: vendorsIdsMap[vw.vendorId] }))
      break;
    case 'Privacy Choice':
      columns = columns.filter(column => column.dataIndex !== 'name');
      hooksModalRecords = props.usPrivacyRegulation.categories;
      break;
  }
  

  if (!props.readOnly) {
    columns.push({
      title: "Actions",
      dataIndex: activeTabKey == 'Vendor' ? "vendorId" : 'name',
      key: "name",
      width: '200px',
      align: 'right',
      className: 'actions',
      render: (id, record, idx) => {
        return (
          <>
            <Button  type="link" icon={<EditOutlined/>} onClick={() => onEditPurpose(record)}/>
            <ConfirmationWrapper
              actionDescription={`remove hooks for this ${activeTabKey}?`}
              deleteBtn={<Tooltip title="Remove Hooks"><Button icon={<DeleteOutlined/>} type="link"/></Tooltip>}
              deleteHandler={() => deleteHooks(record)}
            />
          </>
        );
      },
    });
  }

  let modal = editModal ? (
    <ConsentAndRejectModal
      visible={editModal}
      purpose={editPurpose}
      purposeId={editPurposeId}
      handlePurposeSave={handlePurposeSave}
      closeModal={closeModal}
      readOnly={false}
      records={hooksModalRecords}
      label={activeTabKey}
      isGlobalStandardFlow={isGlobalStandardFlow}
    />
  ) : null;
  
  modal = addNewHookModal ? (
    <ConsentAndRejectModal
      isAddNew={true}
      visible={addNewHookModal}
      purpose={editPurpose}
      purposeId={editPurposeId}
      handlePurposeSave={handlePurposeSave}
      closeModal={closeModal}
      readOnly={false}
      records={hooksModalRecords?.filter(record => !recordHasHooks(record))}
      label={activeTabKey}
      isGlobalStandardFlow={isGlobalStandardFlow}
    />
  ) : modal;
  //debouncing search for performance
  const debouncedChangeHandler = useDebouncedSearch(setSearchValue, 700);

  const applyVendorTypeFilter = (record, vendorTypeFilter) => {
    if (vendorTypeFilter.length === 0) return true;
    const vendorType = getVendorType(record);
    return vendorTypeFilter[0] === vendorType;
  };
  
  const filterRecordsBySearch = (records, searchValue, fieldNames) => {
    if (searchValue.trim().length === 0) return records;
    return records.filter(record =>
      fieldNames.some(fieldName => record[fieldName].toLowerCase().includes(searchValue.trim().toLowerCase()))
    );
  };
  
  const dataSource = useMemo(() => {
    let dataSource = [];
  
    if (activeTabKey === "Vendor") {
      dataSource = props.usPrivacyRegulation.vendorsWrappers
        .map(vw => ({ ...vw, ids: vendorsIdsMap && vendorsIdsMap[vw.vendorId] }))
        .filter(record => applyVendorTypeFilter(record, vendorTypeFilter));
    } else if (activeTabKey === "Privacy Choice") {
      dataSource = props.usPrivacyRegulation.categories;
    }
  
    dataSource = dataSource.filter(record => recordHasHooks(record));
  
    switch (activeTabKey) {
      case 'Vendor':
        dataSource = filterRecordsBySearch(dataSource, searchValue, ['vendorId', 'name']);
        break;
      case 'Privacy Choice':
        dataSource = filterRecordsBySearch(dataSource, searchValue, ['privacyChoice']);
        break;
    }
  
    return dataSource;
  }, [searchValue, activeTabKey, makeDataSourceRender, vendorTypeFilter]) 

  const rowSelection = {
    selectedRowKeys,
    onSelect : (record, selected, selectedRows, nativeEvent) => {
      if(selected) {
        setSelectedRowKeys((selectedKeys) => [... new Set(selectedKeys.concat(activeTabKey == "Vendor" ? record.vendorId : record.id ?? record.privacyChoice))])
      } else {
        setSelectedRowKeys((selectedKeys) => selectedKeys.filter(key => key !== (activeTabKey == "Vendor" ? record.vendorId : (record.id ?? record.privacyChoice))))
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      if(selected){
        setSelectedRowKeys((selectedKeys) => [...new Set(selectedKeys.concat(dataSource.map(v => activeTabKey == "Vendor" ? v.vendorId : v.id ?? v.privacyChoice)))]);
      }else{
        setSelectedRowKeys((selectedKeys) => selectedKeys.filter((key) => !dataSource.map(v => activeTabKey == "Vendor" ? v.vendorId : v.id ?? v.privacyChoice).includes(key)));
      }
    },
    checkStrictly: false
  };


  const onTableChange = (pagination, filters, sorter) => {
    setVendorTypeFilter(filters.name ?? []);
  }

  return (
    <>
      {props.changesDetectedWarning}
      {!props.readOnly ? (
        <>
        <div className="step-header">
          <Typography.Title level={4}>Opt In/Opt Out Hooks</Typography.Title>
        </div>
        <div className="step-breif">
          This section enables you to employ code hooks that are triggered when a user is in either an opted in or opted out state for each of the privacy choices or vendors that you have selected.
          <br/>
         For example, if a user opts out of Targeted Advertising, you can choose to trigger custom JavaScript to notify vendors that they are not permitted to use the user's data for that purpose.
        </div>
        </>
      ) : null}
      <div className="step-content">
      <Tabs
        defaultActiveKey="Vendor"
        onChange={(key) => setActiveTabKey(key)}
        activeKey={activeTabKey}
        // className="spsg-normal opt-in-out-hooks"
      >
        <Tabs.TabPane tab="Vendor" key="Vendor">
        </Tabs.TabPane>
        <Tabs.TabPane tab="Privacy Choice" key="Privacy Choice">
        </Tabs.TabPane>
      </Tabs>
      <div className="vendor-management-table-header-bar">
        <Input.Search
          placeholder={activeTabKey == "Vendor" ? "Search vendor name or ID" : "Search Privacy Choice"}
          type="search"
          onChange={({ target: { value } }) => {
            debouncedChangeHandler(value);
          }}
          style={{width: 360}}
        />
        {!props.readOnly ? (
          <Flex align='center'>
            <Typography.Text disabled={selectedRowKeys.length === 0 } style={{marginRight: `var(--ant-margin-xs)`}}>Bulk actions:</Typography.Text>
            {wrapInInfotip(<Button type='link' icon={<DeleteOutlined/>} disabled={selectedRowKeys.length === 0} onClick={() => setRemoveHooksPrompt(true)}/>, "Remove Hooks")}
            <Divider type="vertical" orientation="center" style={{margin:"0 8px"}}/>
            <Button type="primary" onClick={() => showAddNewHookModal(true)} style={{marginLeft: 12}}> + Add Hook </Button>
          </Flex>
         ) : null}
      </div>
      <Table
        // className={classNames("opt-in-out-hooks", props.readOnly ? "th-white" : "")}
        pagination={{
          position: ['bottomCenter'],
          showTotal: (total) => `Total ${total} items`,
          size:'small',
          defaultPageSize: props.readOnly ? 6 : 7,
          showSizeChanger: true,
          pageSizeOptions: ['7', '10', '20', '50'],
        }}
        columns={columns}
        onChange={onTableChange}
        dataSource={dataSource}
        bordered={false}
        rowKey={(record) => `${activeTabKey == "Vendor" ? record.vendorId : record.id ?? record.privacyChoice}`}
        rowSelection={!props.readOnly && rowSelection}
        locale={{ emptyText: 'No hooks added'}}
        height={'40vh'}
      />
      </div>
      {modal}
      {removeHooksPrompt ? (
        <DismissErrorModal
          modalWrapperClass="operation-confirmation"
          title={"Remove Hooks"}
          error={<><div>Are you sure you want to remove selected hooks?</div></>}
          isModalVisible={removeHooksPrompt}
          handleCancel={() => setRemoveHooksPrompt(false)}
          renderOk={true}
          okText={"Remove"}
          cancelText={"Cancel"}
          handleOk={() => removeHooks()}
          primaryAction="submit"
        />
      ) : null}
    </>
  );
};

export default OptInAndOptOut;
